import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = "/api/expiry";
const ZER_API = "/api/zerodha";

export default function ExpiryDay() {
  const [dashboard, setDashboard] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanSignal, setScanSignal] = useState(null);
  const [autoTrade, setAutoTrade] = useState(false);
  const [trades, setTrades] = useState(() => {
    try { return JSON.parse(localStorage.getItem("expiry_paper_trades") || "[]"); } catch { return []; }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const scanRef = useRef(null);
  const dashRef = useRef(null);
  const processedRef = useRef(new Set());
  const [maxTrades] = useState(3);

  useEffect(() => { localStorage.setItem("expiry_paper_trades", JSON.stringify(trades)); }, [trades]);

  // Fetch dashboard (OI map, walls, max pain)
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard?index=NIFTY`, { timeout: 15000 });
      if (res.data.success) setDashboard(res.data);
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
  }, []);

  // Initial load + refresh every 5 minutes
  useEffect(() => {
    fetchDashboard();
    dashRef.current = setInterval(fetchDashboard, 300000);
    return () => { if (dashRef.current) clearInterval(dashRef.current); };
  }, [fetchDashboard]);

  // Scan loop
  const runScan = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scan?index=NIFTY&timeframe=5minute`, { timeout: 15000 });
      if (res.data.success && res.data.signal) {
        setScanSignal(res.data.signal);

        // Time guard: no trades after 15:00 IST
        const now = new Date();
        const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
        const mins = ist.getHours() * 60 + ist.getMinutes();
        if (mins >= 15 * 60) return;

        // Auto-trade
        if (autoTrade) {
          const sig = res.data.signal;
          const key = `${sig.type}_${sig.setup}_${sig.wall?.strike}_${sig.date}`;
          const todayTrades = trades.filter(t => t.time?.startsWith(new Date().toISOString().split("T")[0]));
          if (!processedRef.current.has(key) && todayTrades.length < maxTrades) {
            processedRef.current.add(key);
            const trade = {
              id: Date.now(), time: new Date().toISOString(), symbol: "NIFTY",
              type: sig.type, setup: sig.setup, entry: sig.entryPrice,
              sl: sig.stopLoss, target: sig.target, risk: sig.risk,
              detail: sig.detail, wall: sig.wall, status: "OPEN",
              exitPrice: null, exitReason: null, pnlPct: null,
            };
            setTrades(prev => [trade, ...prev]);
          }
        }
      } else {
        setScanSignal(null);
      }

      // Monitor open trades against current spot
      const spot = res.data?.spot || dashboard?.spot;
      if (spot) {
        setTrades(prev => prev.map(t => {
          if (t.status !== "OPEN") return t;
          if (t.type === "LONG") {
            if (spot <= t.sl) return { ...t, status: "CLOSED", exitPrice: t.sl, exitReason: "STOP LOSS", pnlPct: pct(t.sl - t.entry, t.entry) };
            if (spot >= t.target) return { ...t, status: "CLOSED", exitPrice: t.target, exitReason: "TARGET HIT", pnlPct: pct(t.target - t.entry, t.entry) };
          } else {
            if (spot >= t.sl) return { ...t, status: "CLOSED", exitPrice: t.sl, exitReason: "STOP LOSS", pnlPct: pct(t.entry - t.sl, t.entry) };
            if (spot <= t.target) return { ...t, status: "CLOSED", exitPrice: t.target, exitReason: "TARGET HIT", pnlPct: pct(t.entry - t.target, t.entry) };
          }
          return t;
        }));
      }

      // Auto-exit at 15:15 IST
      const now2 = new Date();
      const ist2 = new Date(now2.getTime() + (now2.getTimezoneOffset() + 330) * 60000);
      const mins2 = ist2.getHours() * 60 + ist2.getMinutes();
      if (mins2 >= 15 * 60 + 15 && spot) {
        setTrades(prev => prev.map(t => {
          if (t.status !== "OPEN") return t;
          const pnl = t.type === "LONG" ? spot - t.entry : t.entry - spot;
          return { ...t, status: "CLOSED", exitPrice: spot, exitReason: "EOD AUTO-EXIT", pnlPct: pct(pnl, t.entry) };
        }));
      }
    } catch (err) { console.warn("Expiry scan error:", err.message); }
  }, [autoTrade, trades, dashboard, maxTrades]);

  useEffect(() => {
    if (!scanning) { if (scanRef.current) clearInterval(scanRef.current); return; }
    runScan();
    scanRef.current = setInterval(runScan, 30000);
    return () => { if (scanRef.current) clearInterval(scanRef.current); };
  }, [scanning, runScan]);

  function exitTrade(id) {
    const spot = dashboard?.spot;
    setTrades(prev => prev.map(t => {
      if (t.id !== id || t.status !== "OPEN") return t;
      const pnl = t.type === "LONG" ? (spot || t.entry) - t.entry : t.entry - (spot || t.entry);
      return { ...t, status: "CLOSED", exitPrice: spot || t.entry, exitReason: "MANUAL EXIT", pnlPct: pct(pnl, t.entry) };
    }));
  }

  function clearClosed() { setTrades(prev => prev.filter(t => t.status === "OPEN")); }

  const openTrades = trades.filter(t => t.status === "OPEN");
  const closedTrades = trades.filter(t => t.status === "CLOSED");
  const totalPnl = closedTrades.reduce((a, t) => a + (t.pnlPct || 0), 0);

  return (
    <div className="five-ema">
      <h2 style={{ margin: "8px 0", fontSize: 16 }}>Expiry Day — OI Wall Bounce + Gamma Breakout</h2>
      <p style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 12 }}>
        Dedicated to weekly/monthly expiry. Uses live OI walls, max pain drift, and gamma squeeze detection on 5-min candles.
      </p>

      {/* Controls */}
      <div className="ema-filters" style={{ gap: 8 }}>
        <button className={`btn ${scanning ? "btn-danger" : "btn-primary"}`} onClick={() => setScanning(s => !s)}>
          {scanning ? "Stop Scan" : "Start Scan"}
        </button>
        {scanning && (
          <label className={`sim-toggle ${autoTrade ? "sim-off" : "sim-on"}`}>
            <input type="checkbox" checked={autoTrade} onChange={e => setAutoTrade(e.target.checked)} />
            {autoTrade ? "AUTO TRADE ON" : "AUTO TRADE OFF"}
          </label>
        )}
        <button className="btn btn-secondary" onClick={fetchDashboard} disabled={loading}>
          Refresh OI
        </button>
        <span style={{ fontSize: 11, color: "var(--muted2)" }}>Max {maxTrades} trades/day | No entries after 15:00 | Auto-exit 15:15</span>
      </div>

      {error && <div className="scan-error" style={{ margin: "8px 0" }}>{error}</div>}

      {/* Dashboard */}
      {dashboard && (
        <div style={{ marginTop: 16 }}>
          {/* Key metrics bar */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8, marginBottom: 12 }}>
            <MetricCard label="Spot" value={dashboard.spot?.toFixed(0)} />
            <MetricCard label="Max Pain" value={dashboard.maxPain} color={dashboard.spot > dashboard.maxPain + 100 ? "#ef4444" : dashboard.spot < dashboard.maxPain - 100 ? "#22c55e" : "var(--dim2)"} />
            <MetricCard label="PCR" value={dashboard.pcr} color={dashboard.pcr > 1.3 ? "#22c55e" : dashboard.pcr < 0.7 ? "#ef4444" : "var(--text2)"} />
            <MetricCard label="Bias" value={dashboard.bias} color={dashboard.bias === "LONG" ? "#22c55e" : dashboard.bias === "SHORT" ? "#ef4444" : "#f59e0b"} />
            <MetricCard label="Expiry" value={dashboard.expiry} />
            <MetricCard label="Nearest CE Wall" value={dashboard.nearestCeWall ? `${dashboard.nearestCeWall.strike} (${(dashboard.nearestCeWall.oi/1e5).toFixed(0)}L)` : "-"} color="#ef4444" />
            <MetricCard label="Nearest PE Wall" value={dashboard.nearestPeWall ? `${dashboard.nearestPeWall.strike} (${(dashboard.nearestPeWall.oi/1e5).toFixed(0)}L)` : "-"} color="#22c55e" />
          </div>

          {/* Bias explanation */}
          <div style={{ padding: "8px 12px", background: "var(--panel3)", borderRadius: 6, fontSize: 12, color: "var(--dim2)", marginBottom: 12 }}>
            {dashboard.biasReason}
          </div>

          {/* Signal display */}
          {scanSignal && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: scanSignal.type === "LONG" ? "#16532230" : "#ef444420", border: `1px solid ${scanSignal.type === "LONG" ? "#22c55e" : "#ef4444"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className={`signal-badge ${scanSignal.type === "SHORT" ? "badge-short" : "badge-long"}`}>{scanSignal.type}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text2)" }}>{scanSignal.setup}</span>
                <span style={{ fontSize: 12, color: "var(--dim2)" }}>@ {scanSignal.entryPrice?.toFixed(1)}</span>
                <span style={{ fontSize: 11, color: "var(--muted2)", marginLeft: "auto" }}>SL: {scanSignal.stopLoss?.toFixed(1)} | Tgt: {scanSignal.target?.toFixed(1)}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--dim2)", marginTop: 4 }}>{scanSignal.detail}</div>
            </div>
          )}
          {scanning && !scanSignal && (
            <div style={{ padding: "10px 14px", background: "var(--panel3)", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "var(--muted2)", textAlign: "center" }}>
              Scanning 5-min candles for wall-bounce or gamma-breakout signals...
            </div>
          )}

          {/* OI Map table */}
          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--text2)", fontWeight: 600, marginBottom: 8 }}>
              OI Strike Map ({dashboard.strikes?.length} strikes)
            </summary>
            <div className="stocks-table-wrapper" style={{ maxHeight: 400, overflow: "auto" }}>
              <table className="stocks-table">
                <thead><tr><th>Strike</th><th>CE OI</th><th>PE OI</th><th>CE LTP</th><th>PE LTP</th><th>PCR</th><th>Level</th></tr></thead>
                <tbody>
                  {(dashboard.strikes || []).map(s => {
                    const ceOi = (s.ce || {}).oi || 0;
                    const peOi = (s.pe || {}).oi || 0;
                    const isCeWall = dashboard.ceWalls?.some(w => w.strike === s.strike);
                    const isPeWall = dashboard.peWalls?.some(w => w.strike === s.strike);
                    return (
                      <tr key={s.strike} style={{ background: s.isATM ? "var(--border2)" : isCeWall ? "#ef444410" : isPeWall ? "#22c55e10" : undefined }}>
                        <td style={{ fontWeight: s.isATM ? 700 : 400 }}>{s.strike}{s.isATM ? " (ATM)" : ""}</td>
                        <td style={{ color: isCeWall ? "#ef4444" : "var(--text2)", fontWeight: isCeWall ? 700 : 400 }}>{(ceOi/1e5).toFixed(1)}L</td>
                        <td style={{ color: isPeWall ? "#22c55e" : "var(--text2)", fontWeight: isPeWall ? 700 : 400 }}>{(peOi/1e5).toFixed(1)}L</td>
                        <td>{(s.ce || {}).ltp?.toFixed(1) || "-"}</td>
                        <td>{(s.pe || {}).ltp?.toFixed(1) || "-"}</td>
                        <td>{ceOi ? (peOi/ceOi).toFixed(2) : "-"}</td>
                        <td style={{ fontSize: 10 }}>{isCeWall ? "CE WALL" : isPeWall ? "PE WALL" : s.strike === dashboard.maxPain ? "MAX PAIN" : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}

      {/* Trade log */}
      {trades.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, color: "var(--text2)" }}>
              Paper Trades ({openTrades.length} open, {closedTrades.length} closed)
              {closedTrades.length > 0 && <span className={totalPnl >= 0 ? "positive" : "negative"} style={{ marginLeft: 8 }}>Net: {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(3)}%</span>}
            </h4>
            {closedTrades.length > 0 && <button className="btn btn-sm" style={{ fontSize: 10 }} onClick={clearClosed}>Clear Closed</button>}
          </div>
          <div className="stocks-table-wrapper">
            <table className="stocks-table">
              <thead><tr><th>Time</th><th>Setup</th><th>Type</th><th>Entry</th><th>SL</th><th>Target</th><th>P&L</th><th>Reason</th><th>Action</th></tr></thead>
              <tbody>
                {trades.slice(0, 30).map(t => (
                  <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : t.pnlPct >= 0 ? "trade-win" : "trade-loss"}>
                    <td style={{ fontSize: 11 }}>{new Date(t.time).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                    <td style={{ fontSize: 11, fontWeight: 600 }}>{t.setup}</td>
                    <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                    <td>{t.entry?.toFixed(1)}</td>
                    <td className="sl">{t.sl?.toFixed(1)}</td>
                    <td>{t.target?.toFixed(1)}</td>
                    <td className={(t.pnlPct || 0) >= 0 ? "positive" : "negative"}>
                      {t.pnlPct != null ? `${t.pnlPct >= 0 ? "+" : ""}${t.pnlPct}%` : "-"}
                    </td>
                    <td><span className={`exit-reason ${t.exitReason ? "reason-" + (t.pnlPct >= 0 ? "win" : "loss") : ""}`}>{t.exitReason || "OPEN"}</span></td>
                    <td>{t.status === "OPEN" && <button className="btn btn-sm btn-danger" onClick={() => exitTrade(t.id)} style={{ fontSize: 10, padding: "2px 6px" }}>Exit</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!dashboard && !loading && !error && (
        <div style={{ marginTop: 40, textAlign: "center", color: "var(--muted2)" }}>
          <h3 style={{ fontSize: 14 }}>Loading expiry dashboard...</h3>
          <p style={{ fontSize: 12 }}>Requires Zerodha login for live OI data.</p>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: "var(--panel3)", borderRadius: 6, padding: "6px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "var(--dim2)" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: color || "var(--text2)" }}>{value || "-"}</div>
    </div>
  );
}

function pct(pnl, entry) { return Math.round(pnl / entry * 10000) / 100; }
